/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import MicOutlinedIcon from '@mui/icons-material/MicOutlined';
import AudiotrackOutlinedIcon from '@mui/icons-material/AudiotrackOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DocumentScannerOutlinedIcon from '@mui/icons-material/DocumentScannerOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { SUBSCRIPTION_PLANS } from '../constants/subscriptionPlans.js';
import { PRICING_PLANS, PRICING_USAGE } from '../constants/pricingPresentation.js';

const icons = [MicOutlinedIcon, AudiotrackOutlinedIcon, DescriptionOutlinedIcon,
  DocumentScannerOutlinedIcon, HomeOutlinedIcon, AutoAwesomeOutlinedIcon];
const border = '#dbe9eb';
const teal = '#12888a';

// The caller supplies checkout and eligibility. This component never grants,
// charges, or changes a subscription itself.
export default function PricingPlans({ currentPlan, currentCadence, onPlanSelected,
  showFreePlan = false, commitmentLocked = false, billingUnavailable = false, busy = false }) {
  const normalizedCadence = currentCadence === 'annual' ? 'annual' : 'monthly';
  const [cadence, setCadence] = useState(normalizedCadence);
  const blocked = (plan) => busy || billingUnavailable ||
    (commitmentLocked && (plan.tier === 'free' || cadence === 'monthly'));
  const current = (plan) => currentPlan?.toLowerCase() === plan.tier &&
    (plan.tier === 'free' || cadence === normalizedCadence);
  const select = (plan) => {
    if (!blocked(plan) && !current(plan)) onPlanSelected(plan, plan.tier === 'free' ? 'monthly' : cadence);
  };
  return <Box sx={{ color: '#182732' }}>
    <Typography sx={{ color: teal, textTransform: 'uppercase', letterSpacing: '.13em', fontSize: 12, fontWeight: 800 }}>Plans &amp; credits</Typography>
    <Typography variant="h4" component="h2" sx={{ mt: 1, mb: 1, fontWeight: 700, fontSize: { xs: 28, md: 32 } }}>Choose your plan</Typography>
    <Typography color="text.secondary" sx={{ mb: 3 }}>All plans include secure data storage, privacy protection and product updates.</Typography>
    <Stack direction="row" spacing={0.5} role="group" aria-label="Billing term"
      sx={{ display: 'inline-flex', p: 0.5, background: '#eaf3f4', border: '1px solid #d4e5e6', borderRadius: 3, mb: 2.5 }}>
      {['monthly', 'annual'].map(term => <Button key={term} aria-pressed={cadence === term}
        disabled={busy || billingUnavailable || (commitmentLocked && term === 'monthly')}
        onClick={() => setCadence(term)} sx={{ px: 2, py: 1, color: cadence === term ? teal : '#526b73',
          background: cadence === term ? 'white' : 'transparent', borderRadius: 2, textTransform: 'none', fontWeight: 700 }}>
        {term === 'monthly' ? 'Monthly' : 'Annual · 10% off'}
      </Button>)}
    </Stack>
    {commitmentLocked && <Alert severity="info" sx={{ mb: 2 }}>During the 12-month annual commitment you cannot cancel, switch to Free, or switch to Monthly.</Alert>}
    {billingUnavailable && <Alert severity="warning" sx={{ mb: 2 }}>Refresh Subscription Details to confirm your subscription before changing plans.</Alert>}
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: `repeat(${showFreePlan ? 3 : 2}, minmax(0, 1fr))` }, gap: 1.75 }}>
      {PRICING_PLANS.filter(copy => showFreePlan || copy.name !== 'Free').map(copy => {
        const plan = SUBSCRIPTION_PLANS[copy.name.toUpperCase()];
        const isFree = plan.tier === 'free';
        const featured = plan.tier === 'pro';
        const disabled = blocked(plan) || current(plan);
        const details = isFree || cadence === 'monthly' ? copy.details :
          copy.details.filter((_, index) => index < (featured ? 3 : 2)).map(line =>
            line === 'Cancel anytime' ? '12-month commitment, billed monthly; cannot cancel during the term.' : line);
        return <Box key={plan.tier} component="article" aria-label={`${plan.name} plan`}
          sx={{ display: 'flex', flexDirection: 'column', p: 2.5, border: featured ? '2px solid #1aa9a9' : `1px solid ${border}`,
            borderRadius: 4, background: 'white', boxShadow: featured ? '0 8px 24px #007c8115' : '0 4px 18px #17455108' }}>
          <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1} justifyContent="space-between">
            <Typography component="h3" fontSize={22} fontWeight={700}>{plan.name}</Typography>
            {(isFree || featured) && <Chip size="small" label={isFree ? '1-month trial' : 'Most popular'}
              sx={{ background: isFree ? '#ddf5f4' : '#e9e5fb', color: isFree ? teal : '#644fc4', fontSize: 12 }} />}
          </Stack>
          {current(plan) && <Typography variant="caption" sx={{ mt: 1, color: teal, fontWeight: 700 }}>Current plan</Typography>}
          <Typography variant="body2" color="text.secondary" sx={{ my: 2, minHeight: { md: 60 } }}>{copy.audience}</Typography>
          <Typography fontSize={30} fontWeight={800}>
            {cadence === 'annual' && !isFree ? plan.annual.displayPrice : plan.displayPrice}
            <Typography component="span" fontSize={14} color="text.secondary"> {isFree ? 'one-time trial' : '/ month'}</Typography>
          </Typography>
          <Typography sx={{ mt: 2.5, pt: 2, borderTop: '1px solid #e6eef0', color: '#45636b', fontSize: 14 }}>
            <Typography component="strong" fontSize={21} fontWeight={700} color={teal}>{plan.monthlyCredits} credits</Typography> {isFree ? 'included once' : 'included monthly'}
          </Typography>
          <Box component="ul" sx={{ pl: 2.5, my: 2.5, color: '#536a73', fontSize: 14, lineHeight: 1.55,
            '& li': { mb: 1 }, '& li::marker': { color: '#14a3a5' } }}>
            {details.map(line => <li key={line}>{line}</li>)}
          </Box>
          <Button variant={featured ? 'contained' : 'outlined'} disabled={disabled} onClick={() => select(plan)}
            aria-label={current(plan) ? `${plan.name} current plan` : isFree ? 'Continue with Free' : `Choose this plan: ${plan.name}`}
            sx={{ mt: 'auto', width: '100%', minHeight: 48, borderRadius: 2.5, textTransform: 'none', fontWeight: 700,
              ...(featured ? { background: '#20adac', '&:hover': { background: teal } } : { color: teal, borderColor: '#1ca9aa' }) }}>
            {busy && isFree ? <CircularProgress size={20} /> : current(plan) ? 'Current plan' : isFree ? 'Continue with Free' : 'Choose this plan'}
          </Button>
        </Box>;
      })}
    </Box>
    <Box sx={{ mt: 4.5 }}>
      <Typography component="h3" fontSize={21} fontWeight={700}>How credits are used</Typography>
      <Typography color="text.secondary" sx={{ mt: 0.75, mb: 2 }}>Your balance is shared across features, so you can use it where it helps most.</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.75 }}>
        {PRICING_USAGE.map((item, index) => {
          const Icon = icons[index];
          return <Box key={item.title} sx={{ p: 2.5, border: `1px solid ${border}`, borderRadius: 3.5,
            display: 'grid', gridTemplateColumns: '42px minmax(0, 1fr)', gap: 1.75, background: 'white' }}>
            <Box sx={{ background: '#e2f5f4', color: '#098c90', width: 42, height: 42, borderRadius: 2.5, display: 'grid', placeItems: 'center' }}><Icon /></Box>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography component="h4" fontWeight={700}>{item.title}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, mb: 1.75 }}>{item.description}</Typography>
              <Typography variant="body2" fontWeight={700} color={teal} sx={{ mt: 'auto' }}>{item.cost}</Typography>
            </Box>
          </Box>;
        })}
      </Box>
    </Box>
    <Box sx={{ mt: 4.5, p: 3, borderRadius: 4, background: '#eaf5f5', border: '1px solid #d0e7e7' }}>
      <Typography component="h3" fontSize={21} fontWeight={700}>Need more credits? Recharge anytime.</Typography>
      <Typography sx={{ mt: 1, color: '#4e6a72' }}>Buy a one-off credit pack when your balance runs low. Credits are added to your wallet once payment is confirmed, then used alongside your plan credits. Each extra credit costs HK$5.</Typography>
      <Stack direction="row" flexWrap="wrap" gap={1} sx={{ my: 2 }}>
        {[20, 40, 80, 160].map(credits => <Box key={credits} sx={{ px: 1.75, py: 1.5, background: 'white', border: '1px solid #cce2e3', borderRadius: 2.5 }}>
          <Typography fontWeight={700}>{credits} credits</Typography><Typography variant="body2" color="text.secondary">HK${credits * 5}</Typography>
        </Box>)}
      </Stack>
      <Typography variant="body2" color="text.secondary">You can connect as many clients as your available credits cover.</Typography>
    </Box>
    {showFreePlan && <Typography variant="body2" sx={{ mt: 3, color: '#4c6870' }}>
      <strong>About your free trial:</strong> Your 15 credits can be used during the first month. Unused trial credits expire at the end of that month; your account stays active, and you can upgrade to Basic or Pro anytime.
    </Typography>}
  </Box>;
}
